<template>
  <div class="work-order-processing-progress">
      <div class="progress-container">
        <div class="chart-section">
          <div ref="chartRef" class="chart"></div>
        </div>
        
        <div class="progress-list">
          <div class="progress-item" v-for="(item, index) in progressList" :key="item.type">
            <div class="progress-header">
              <span class="type-name">{{ item.typeName }}</span>
              <span class="type-count">{{ formatNumber(item.typeTotal) }} {{ $t('件') }}</span>
            </div>
            <div class="progress-bar-container">
              <div class="progress-bar">
                <div 
                  class="progress-fill" 
                  :class="getProgressClass(index)"
                  :style="{ width: item.rate + '%' }"
                >
                  <span class="progress-text">{{ item.rate }}%</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
  </div>
</template>

<script>
import * as echarts from 'echarts'
import { postAction } from '@/api/manage'
export default {
  name: 'WorkOrderProcessingProgress',
  components: {

  },
  data() {
    return {
      chart: null,
      progressList: [],
      colorPalette: [
        '#00E4FF', // 青绿色
        '#FFB800', // 橙黄色  
        '#019AF3', // 蓝色
        '#52C4DF', // 浅青绿
        '#FFA726'  // 橙红
      ],
      url: {
        queryProgressOfTheMonthByEmergency: "/jeecg-engineering/ticket/index/largeScreen/queryProgressOfTheMonthByEmergency"
      },
      chartData: [],
      totalCount: 0
    }
  },
  mounted() {
    this.init()
  },
  beforeDestroy() {
    this.disposeChart()
  },
  methods: {
    async init() {
      await this.getData()
      this.initChart()
    },
    async getData() {
      try {
        const res = await postAction(this.url.queryProgressOfTheMonthByEmergency, {})
        if (res.success && res.result) {
          const data = res.result
          this.totalCount = data.total || 0
          
          this.progressList = data.progressOfTheMonthByEmergencyInnerVOS?.map(item => ({
            typeName: item.typeName,
            typeTotal: item.typeTotal || 0,
            type: item.type,
            rate: data.total > 0 ? Number(((item.typeTotal / data.total) * 100).toFixed(1)) : 0
          })) || []
          
          this.chartData = this.progressList.map(item => ({
            name: item.typeName,
            value: item.typeTotal || 0
          }))
        } else {
          this.loadMockData()
        }
      } catch (error) {
        console.error('获取工单处理进度数据失败:', error)
        this.loadMockData()
      }
    },
    loadMockData() {
      this.totalCount = 1245
      this.progressList = [
        { typeName: '紧急故障', typeTotal: 423, type: 0, rate: 34.0 },
        { typeName: '常规维修', typeTotal: 356, type: 1, rate: 28.6 },
        { typeName: '设备更换', typeTotal: 267, type: 2, rate: 21.4 },
        { typeName: '安装服务', typeTotal: 199, type: 3, rate: 16.0 }
      ]
      
      this.chartData = this.progressList.map(item => ({
        name: item.typeName,
        value: item.typeTotal
      }))
    },
    initChart() {
      this.disposeChart()
      
      if (!this.$refs.chartRef) return
      
      this.chart = echarts.init(this.$refs.chartRef)
      
      const option = {
        title: {
          text: `{total|${this.totalCount}}{unit|${this.$t('件')}}`,
          subtext: this.$t('本月工单总数'),
          left: 'center',
          top: '35%',
          textStyle: {
            color: '#8BA6C6',
            fontSize: 20,
            fontWeight: 'bold',
            rich: {
              total: {
                color: '#00ffc8',
                fontSize: 28,
                fontWeight: 'bold',
                fontFamily: 'ds-digital-bold'
              },
              unit: {
                color: '#8BA6C6',
                fontSize: 12,
                padding: [0, 0, 0, 5]
              }
            }
          },
          subtextStyle: {
            color: '#8BA6C6',
            fontSize: 12,
            padding: [5, 0, 0, 0]
          }
        },
        tooltip: {
          trigger: 'item',
          backgroundColor: 'rgba(0,11,42,0.9)',
          borderColor: '#5D6A94',
          borderWidth: 1,
          borderRadius: 8,
          textStyle: {
            color: '#CEE2FF'
          },
          formatter: (params) => {
            return `
              <div style="padding: 8px;">
                <div style="font-size: 14px; margin-bottom: 5px; color: #fff;">${params.name}</div>
                <div style="font-size: 12px; color: #00ffc8;">
                  ${this.$t('数量')}: ${params.value} ${this.$t('件')}
                </div>
                <div style="font-size: 12px; color: #00ffc8;">
                  ${this.$t('占比')}: ${params.percent}%
                </div>
              </div>
            `
          }
        },
        legend: {
          show: false
        },
        series: [
          {
            name: this.$t('工单处理进度'),
            type: 'pie',
            radius: ['50%', '80%'],
            center: ['50%', '50%'],
            avoidLabelOverlap: false,
            itemStyle: {
              borderRadius: 2,
              borderColor: 'transparent',
              borderWidth: 0
            },
            label: {
              show: false
            },
            emphasis: {
              label: {
                show: true,
                fontSize: 12,
                fontWeight: 'bold',
                color: '#fff'
              },
              itemStyle: {
                shadowBlur: 0,
                shadowOffsetX: 0,
                shadowColor: 'transparent'
              }
            },
            labelLine: {
              show: false
            },
            data: this.chartData.map((item, index) => ({
              ...item,
              itemStyle: {
                color: this.colorPalette[index % this.colorPalette.length]
              }
            }))
          }
        ]
      }
      
      this.chart.setOption(option)
      
      // 响应式处理
      window.addEventListener('resize', this.handleResize)
    },
    adjustColor(color, amount) {
      // 简单的颜色调整函数
      const hex = color.replace('#', '')
      const r = Math.max(0, Math.min(255, parseInt(hex.substr(0, 2), 16) + Math.floor(amount * 255)))
      const g = Math.max(0, Math.min(255, parseInt(hex.substr(2, 2), 16) + Math.floor(amount * 255)))
      const b = Math.max(0, Math.min(255, parseInt(hex.substr(4, 2), 16) + Math.floor(amount * 255)))
      return `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`
    },
    getProgressClass(index) {
      const classes = ['progress-blue', 'progress-green', 'progress-yellow', 'progress-red', 'progress-purple']
      return classes[index % classes.length]
    },
    formatNumber(num) {
      return num.toLocaleString()
    },
    handleResize() {
      if (this.chart) {
        this.chart.resize()
      }
    },
    disposeChart() {
      if (this.chart) {
        this.chart.dispose()
        this.chart = null
        window.removeEventListener('resize', this.handleResize)
      }
    }
  }
}
</script>

<style lang="less" scoped>
.work-order-processing-progress {
  height: 100%;
  padding: 10px;
}

.progress-container {
  display: flex;
  height: 100%;
  gap: 10px;
}

.chart-section {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
}

.chart {
  width: 100%;
  height: 100%;
}

.progress-list {
  flex: 0 0 35%;
  display: flex;
  flex-direction: column;
  justify-content: space-evenly;
  gap: 4px;
}

.progress-item {
  background: rgba(0, 48, 111, 0.2);
  border: 1px solid rgba(0, 170, 255, 0.15);
  border-radius: 4px;
  padding: 5px 6px;
  transition: all 0.2s ease;
  
  &:hover {
    background: rgba(0, 48, 111, 0.4);
    border-color: rgba(0, 170, 255, 0.3);
  }
}

.progress-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 4px;
}

.type-name {
  color: #b6deff;
  font-size: 13px;
  font-weight: 500;
}

.type-count {
  color: #52C4DF;
  font-size: 12px;
  font-weight: bold;
}

.progress-bar-container {
  position: relative;
}

.progress-bar {
  width: 100%;
  height: 8px;
  background: rgba(8, 48, 111, 0.6);
  border-radius: 4px;
  overflow: hidden;
}

.progress-fill {
  height: 100%;
  border-radius: 4px;
  transition: width 0.8s ease;
  position: relative;
  
  &.progress-blue {
    background: linear-gradient(90deg, #00E4FF 0%, #0099CC 100%);
  }
  
  &.progress-green {
    background: linear-gradient(90deg, #00FF88 0%, #00CC66 100%);
  }
  
  &.progress-yellow {
    background: linear-gradient(90deg, #FFB800 0%, #FF8C00 100%);
  }
  
  &.progress-red {
    background: linear-gradient(90deg, #FF4D4F 0%, #CC3333 100%);
  }
  
  &.progress-purple {
    background: linear-gradient(90deg, #722ED1 0%, #531DAB 100%);
  }
}

.progress-text {
  position: absolute;
  right: 5px;
  top: 50%;
  transform: translateY(-50%);
  color: #fff;
  font-size: 10px;
  font-weight: bold;
}

@media (max-width: 1600px) {
  .progress-container {
    flex-direction: column;
  }
  
  .chart-section {
    flex: none;
    height: 50%;
  }
  
  .progress-list {
    flex: none;
    height: 50%;
    gap: 10px;
  }
  
  .progress-item {
    padding: 8px;
  }
}

@media (max-width: 1200px) {
  .chart-section {
    height: 45%;
  }
  
  .progress-list {
    height: 55%;
    gap: 8px;
  }
  
  .type-name {
    font-size: 11px;
  }
  
  .type-count {
    font-size: 11px;
  }
}
</style>